/**
 * Open-item counts per Management tab (G-10 red count badges).
 *
 * Each of CORE-M-02..CORE-M-08 replaces its tab's stub with a real fetcher
 * (react-query, workspace-scoped) as its content lands. Keeping the seven
 * counts wired through one hook so the bar has one predictable dependency,
 * and so bus-factor readers see the whole count wiring in one place.
 */
import { useQuery } from '@tanstack/react-query';

import {
  getAdMonitor,
  listStrategyGoogleRecs,
} from '@/services/googleAdsManagementService';
import {
  getOperatorQueueCounts,
  operatorQueueCountsQueryKey,
} from '@/services/googleAdsOperatorQueueService';
import {
  campaignReviewQueryKey,
  getCampaignReview,
} from '@/services/googleAdsCampaignReviewService';

import type { ManagementTabSlug } from './tabSlugs';
import { useManagementWindowDays } from './useManagementWindow';

// Gap A / CORE-M-12: was a local `const AD_REVIEW_DAYS = 30`. The badge has
// to be counted over the same window as the tab it labels, or the number in
// the bar disagrees with the rows underneath it.

export type ManagementCountMap = Record<ManagementTabSlug, number>;

export function useManagementCounts(tenantId: string | null): ManagementCountMap {
  const windowDays = useManagementWindowDays();
  // CORE-M-02: real count from the Strategy recs endpoint. The tab body
  // subscribes to the same queryKey, so this is a shared cached read: one
  // network trip per workspace, one number in the badge.
  const googleRecsQuery = useQuery({
    queryKey: ['management-google-recs', tenantId],
    queryFn: () => listStrategyGoogleRecs(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  // CORE-M-03: search_terms_easy count from the shared operator worklist
  // counts endpoint. The Search Terms tab already invalidates this key on
  // every apply, so the badge drops immediately when negatives are applied.
  const queueCountsQuery = useQuery({
    queryKey: operatorQueueCountsQueryKey(tenantId ?? ''),
    queryFn: () => getOperatorQueueCounts(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  // CORE-M-06: waste_count from the ad-monitor read. Shares the same
  // queryKey as the Ad Review tab body, so it's one cached read per
  // workspace.
  const adMonitorQuery = useQuery({
    queryKey: ['ga-ad-monitor', tenantId, windowDays],
    queryFn: () => getAdMonitor(tenantId ?? '', windowDays),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  // CORE-M-04: flagged campaigns from the Campaign Review read, same query
  // key as the tab body. Zero while tracking has an open problem, because the
  // tab flags nothing then.
  const campaignReviewQuery = useQuery({
    queryKey: campaignReviewQueryKey(tenantId),
    queryFn: () => getCampaignReview(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const campaignReview = campaignReviewQuery.data;
  const campaignReviewCount =
    campaignReview?.read_ok && !campaignReview.tracking_quiet
      ? campaignReview.flagged.length
      : 0;

  return {
    'google-recommendations': googleRecsQuery.data?.total ?? 0,
    'negatives': queueCountsQuery.data?.search_terms_easy ?? 0,
    'campaign-review': campaignReviewCount,
    'ad-group-review': 0,
    'ad-review': adMonitorQuery.data?.waste_count ?? 0,
    'keyword-review': 0,
    'budget-reallocation': 0,
  };
}
